import type { User } from '@/_types/models/user';
import type { TaxReturnStatusType } from '@/_types/taxReturnDelivery';

export interface Voucher {
  amount: number;
  custom_payment_name?: string | null;
  due_date: string;
  id?: number;
  is_due_asap: boolean;
  is_paid: boolean;
  page: number;
  payment_link: string;
  reviewed_by: number;
  status: string;
  voucher_id: string | null;
  quarter?: string | null;
  is_instruction_page?: boolean;
}

export type TaxReturnDraftStatusType =
  | 'draft'
  | 'approved'
  | 'change_requested';

export interface VoucherDefinition {
  client_facing_name: string;
  code: string;
  id: string;
  name: string;
  payment_link: string;
}

export interface TaxReturnSignature {
  name: string;
  page: number;
  pageHeight: number;
  pageWidth: number;
  signatureBottom: number;
  signatureData: string;
  signatureDateBottom: number;
  signatureDateHeight: number;
  signatureDateLeft: number;
  signatureDateWidth: number;
  signatureHeight: number;
  signatureLeft: number;
  signatureTextSize: number;
  signatureWidth: number;
  title: string;
  userId: number;
  isMarkedSigned: boolean;
}

export interface TaxReturnEsignForm {
  form_id: string;
  form_name: string;
  custom_form_name?: string;
  id: number;
  page: number;
  reviewed_by: number;
  signatures: Array<TaxReturnSignature>;
  wetSignatures: Array<any>;
  wetSignaturesRequired: boolean;
}

export interface TaxReturnPaperFiledReturn {
  custom_return_name: string;
  document_path: string;
  is_mailed: boolean;
  instructions: string;
  mailing_address: string;
}

export type IdentityVerificationStatus =
  | 'not_started'
  | 'requires_input'
  | 'processing'
  | 'verified'
  | 'canceled'
  | 'failed';

export type K1DistributionStatus =
  | 'not_applicable'
  | 'no_k1_packages'
  | 'sent_to_partner'
  | 'not_sent_to_partner'
  | 'partially_sent_to_partner';

export type IdentityVerificationErrorCode =
  | 'abandoned'
  | 'consent_declined'
  | 'country_not_supported'
  | 'device_not_supported'
  | 'document_expired'
  | 'document_type_not_supported'
  | 'document_unverified_other'
  | 'email_unverified_other'
  | 'email_verification_declined'
  | 'id_number_insufficient_document_data'
  | 'id_number_mismatch'
  | 'id_number_unverified_other'
  | 'phone_unverified_other'
  | 'phone_verification_declined'
  | 'selfie_document_missing_photo'
  | 'selfie_face_mismatch'
  | 'selfie_manipulated'
  | 'selfie_unverified_other'
  | 'under_supported_age';

export interface IdentityVerification {
  status: IdentityVerificationStatus | null | undefined;
  error_code: IdentityVerificationErrorCode | null | undefined;
  error_description: string | null | undefined;
  user_id?: number;
  stripe_verification_session_created_at?: string;
}

export interface TaxReturnIdentityVerification {
  name: string;
  phone_number: string;
  title: string;
  phone_number_verified: boolean;
  updated_at: string;
  user_id: number;
  id: number;
}

export interface Signer extends Pick<User, 'id' | 'fname' | 'lname' | 'email'> {
  role: 'primary' | 'spouse';
}

export interface K1Package {
  document_path: string;
  partner_email: string;
  partner_name: string;
  custom_package_name: string;
  sent_at_to_partner: number | null;
  viewed_at_by_partner: number | null;
  consented_to_electronic_delivery: boolean | null;
  marked_delivered_at: number | null;
}

export interface TaxReturn {
  all_paid: boolean;
  all_signed: boolean;
  assigned_advisor_id: number;
  company_id: number;
  company_name: string;
  company_hash: string;
  completed_payment_count: number;
  completed_signature_count: number;
  created_at: string;
  current_status: TaxReturnStatusType;
  draft_status: TaxReturnDraftStatusType | null;
  draft_comment: string | null;
  e_sign_forms: Array<TaxReturnEsignForm>;
  external_entity_id?: string;
  filing_instructions: Array<{ page: number }>;
  k1_packages: Array<K1Package>;
  k1_distribution_status: K1DistributionStatus;
  id: number;
  original_file: string;
  last_reminder_sent_at: string;
  last_signed_at?: string | null;
  message: string;
  paper_filed_returns: Array<TaxReturnPaperFiledReturn>;
  payment?: { status: string; amount: number; currency: string } | null;
  payment_vouchers: Array<Voucher>;
  remaining_payment_count: number;
  remaining_signature_count: number;
  reminder_disabled: boolean;
  signers?: Array<Signer>;
  sorted_payment_vouchers: Array<Voucher>;
  status: TaxReturnStatusType;
  stripe_identity_verifications?: IdentityVerification[];
  tax_return_identity_verifications?: TaxReturnIdentityVerification[];
  subject: string;
  tax_return_name: string;
  tax_return_pages: Array<{ page: number }>;
  tax_year: number;
  total_payment_count: number;
  total_signature_count: number;
  transmittal_letters: Array<{ page: number }>;
  updated_at: string;
  watermarked_file: string;
  customization: {
    reminder_email_subject?: string;
    reminder_email_message?: string;
  };
}

export type K1PackageStatus =
  | 'declined'
  | 'viewed'
  | 'sent'
  | 'delivered'
  | 'not_sent';

export interface K1PackageStatusInfo {
  status: K1PackageStatus;
  label: string;
  intent: 'danger' | 'success' | 'default' | 'warning';
}

export const getK1PackageStatusInfo = (pkg: K1Package): K1PackageStatusInfo => {
  if (pkg.consented_to_electronic_delivery === false) {
    return {
      status: 'declined',
      label: 'Declined e-delivery',
      intent: 'danger',
    };
  } else if (pkg.viewed_at_by_partner) {
    const viewedDate = new Date(pkg.viewed_at_by_partner * 1000);
    return {
      status: 'viewed',
      label: `Viewed on ${viewedDate.toLocaleDateString()}`,
      intent: 'success',
    };
  } else if (pkg.sent_at_to_partner) {
    const sentDate = new Date(pkg.sent_at_to_partner * 1000);
    return {
      status: 'sent',
      label: `Sent on ${sentDate.toLocaleDateString()}`,
      intent: 'default',
    };
  } else if (pkg.marked_delivered_at) {
    return {
      status: 'delivered',
      label: 'Marked Delivered',
      intent: 'success',
    };
  } else {
    return {
      status: 'not_sent',
      label: 'Not Sent',
      intent: 'warning',
    };
  }
};

// Utility functions for checking K-1 package states
export const isK1PackageSentOrDelivered = (pkg: K1Package): boolean => {
  return !!pkg.sent_at_to_partner || !!pkg.marked_delivered_at;
};

export const isK1PackageEditable = (pkg: K1Package): boolean => {
  return (
    !pkg.viewed_at_by_partner &&
    (pkg.consented_to_electronic_delivery === undefined ||
      pkg.consented_to_electronic_delivery === null)
  );
};

export const isK1PackageSendable = (pkg: K1Package): boolean => {
  return (
    !pkg.viewed_at_by_partner && pkg.consented_to_electronic_delivery !== false
  );
};

export const isK1PackageMarkDeliverable = (pkg: K1Package): boolean => {
  return !pkg.viewed_at_by_partner;
};
